import { useState } from 'react'
import { Link } from 'react-router-dom'
import { X, ArrowRight } from 'lucide-react'
import type { InteractiveFeaturedCategory } from '@/data/helpData'

interface CategoryTopicsModalProps {
  category: InteractiveFeaturedCategory
  isOpen: boolean
  onClose: () => void
}

export default function CategoryTopicsModal ({ category, isOpen, onClose }: CategoryTopicsModalProps) {
  if (!isOpen || !category.topics) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-purple-900 dark:text-purple-300">
              {category.name}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {category.description}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Topics List */}
        <div className="p-6">
          <div className="space-y-3">
            {category.topics.map((topic) => (
              <Link
                key={topic.id}
                to={topic.link}
                onClick={onClose}
                className="group block p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-purple-700 dark:group-hover:text-purple-300 mb-1">
                      {topic.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {topic.description}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-purple-500 transition-colors ml-4 flex-shrink-0" />
                </div>
              </Link>
            ))}
          </div>

          {/* View All Articles Link */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Link
              to="/help/articles?category=billing"
              onClick={onClose}
              className="inline-flex items-center text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 font-medium"
            >
              View all {category.name.toLowerCase()} articles
              <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
